import type { ReactNode } from "react";

import { MarketingLink } from "@/components/marketing/MarketingButton";
import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";
import {
  ENHANCED_ALBUMS_LIVE,
  MARKETING_PRODUCTS,
} from "@/components/marketing/products";
import { cn } from "@/lib/utils";

type Format = {
  to: string;
  label: ReactNode;
  title: string;
  body: string;
  cta: string;
  tone: string;
};

const ALL_FORMATS: Format[] = [
  {
    to: MARKETING_PRODUCTS.super.path,
    label: (
      <>
        Super Single
        <sup className="ml-[4px] align-top font-figtree text-[10px] font-extrabold leading-none text-marketing-ink">
          TM
        </sup>
      </>
    ),
    title: "Make one song feel bigger.",
    body: "Package the original song with its clean version, instrumental, acapella, video, or other exclusive content. Fans get more ways to experience the track, and you have more value to sell.",
    cta: "Explore Super Singles →",
    tone: "bg-[#EEF1FF] after:bg-[#AFB9FF]",
  },
  {
    to: MARKETING_PRODUCTS.enhancedAlbums.path,
    label: "Enhanced Albums",
    title: "Give every version its place.",
    body: "Bring the original, clean, instrumental, and acapella versions of an album together in one organized experience. Add bonus tracks or behind-the-scenes content to make the release even more meaningful.",
    cta: "Explore Enhanced Albums →",
    tone: "bg-[#FFF0F6] after:bg-[#FFAFD1]",
  },
];

const FORMATS = ALL_FORMATS.filter(
  (format) =>
    ENHANCED_ALBUMS_LIVE ||
    format.to !== MARKETING_PRODUCTS.enhancedAlbums.path,
);

export default function SellFormats() {
  return (
    <MarketingSection className="bg-marketing-soft">
      <SectionKicker>More than the standard release</SectionKicker>
      <SectionTitle>Give fans more worth paying for.</SectionTitle>
      <SectionIntro className="!max-w-[760px]">
        Turn the music you already made into a more valuable fan experience with
        alternate versions and exclusive content.
      </SectionIntro>
      <div
        className={cn(
          "mt-[44px] grid gap-6 max-[680px]:grid-cols-1",
          FORMATS.length > 1 && "grid-cols-2",
        )}
      >
        {FORMATS.map((format) => (
          <MarketingLink
            key={format.to}
            to={format.to}
            className={cn(
              "relative block min-h-[360px] overflow-hidden rounded-[26px] border border-[#DCE2EF] px-[42px] pb-20 pt-[42px] outline-none transition-[transform,box-shadow,border-color] duration-[240ms] ease-[ease] after:absolute after:-bottom-[135px] after:-right-[110px] after:size-[250px] after:rounded-full after:opacity-[0.42] after:content-[''] hover:border-[#C8D0E4] hover:shadow-[0_22px_48px_rgba(13,20,52,0.11)] focus-visible:border-[#C8D0E4] focus-visible:shadow-[0_22px_48px_rgba(13,20,52,0.11)] motion-safe:hover:-translate-y-[6px] motion-safe:focus-visible:-translate-y-[6px] max-[680px]:min-h-[390px] max-[680px]:px-[30px] max-[680px]:pb-[72px] max-[680px]:pt-[30px]",
              format.tone,
            )}
          >
            <div className="relative z-[1] font-permanent-marker text-[30px] font-normal leading-none text-blue-600">
              {format.label}
            </div>
            <h3 className="relative z-[1] mt-[25px] max-w-[520px] font-figtree text-[32px] font-[850] leading-[1.08] tracking-[-0.035em] text-marketing-ink">
              {format.title}
            </h3>
            <p className="relative z-[1] mt-[18px] max-w-[560px] font-open-sans text-[17px] leading-[1.65] text-[#59637A]">
              {format.body}
            </p>
            <span className="absolute bottom-[38px] left-[42px] z-[1] font-figtree text-[15px] font-extrabold leading-none text-blue-600 max-[680px]:bottom-[30px] max-[680px]:left-[30px]">
              {format.cta}
            </span>
          </MarketingLink>
        ))}
      </div>
    </MarketingSection>
  );
}
